import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ToggleGroup, tokens, Text, Tooltip } from '@hotjar/hotsauce';
import { moment } from '@hotjar/webapp/lib/moment';
import type { DateRange } from '@hotjar/global-filters';
import { DATE_RANGE_TYPE } from '@hotjar/global-filters';
import { useTranslation } from '@hotjar/translations';
import { NPSTag } from '@hotjar/feedback/components/Surveys/Results/NPSView/NPSTag';
import type { NPSResolutionChoice } from './resolutions';
import { getFirstViableResolution, isResolutionViable, resolutionChoices } from './resolutions';
import { NPSChart } from './NPSChart';

const Container = styled.div`
  margin-bottom: ${tokens.spacer.x3};
`;
const Resolutions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${tokens.spacer.x1};
`;
const StyledText = styled(Text)`
  margin-bottom: 0;
`;

type DateRangeUnit = 'hours' | 'days' | 'months';

function getNumberOfDays(dateRangeSettings: DateRange) {
  if (dateRangeSettings.type === DATE_RANGE_TYPE.RELATIVE) {
    if (dateRangeSettings.unit === 'hours') {
      return dateRangeSettings.amount / 24;
    }
    if (dateRangeSettings.unit === 'days') {
      return dateRangeSettings.amount;
    }
    if (dateRangeSettings.unit === 'months') {
      return dateRangeSettings.amount * 30;
    }
  }

  if (dateRangeSettings.type === DATE_RANGE_TYPE.ABSOLUTE) {
    return moment.utc(dateRangeSettings.to).diff(moment.utc(dateRangeSettings.from), 'days');
  }

  if (dateRangeSettings.type === DATE_RANGE_TYPE.ALL) {
    return undefined;
  }
  throw new Error('Unknown date type');
}

export interface RelativeDateRangeSettings {
  type: 'date_relative';
  value: number;
  unit: DateRangeUnit;
}

export interface AbsoluteDateRangeSettings {
  type: 'date_absolute';
  from: Date;
  to: Date;
}

export type DateRangeSettings = RelativeDateRangeSettings | AbsoluteDateRangeSettings;

export interface NPSLineChartWithResolutionProps {
  dateRangeSettings: DateRange | null;
  scoresPerResolution: {
    scorePerMonth: Record<string, number>;
    scorePerWeek: Record<string, number>;
    scorePerDay: Record<string, number>;
  } | null;
}

export function NPSLineChartWithResolution({
  dateRangeSettings,
  scoresPerResolution,
}: NPSLineChartWithResolutionProps): JSX.Element {
  const { t } = useTranslation('feedback', { keyPrefix: 'results.nps-view-mode' });
  const [dateRangeInDays, setDateRangeInDays] = useState<number | undefined>();
  const [resolution, setResolution] = useState<NPSResolutionChoice>(resolutionChoices[1]);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dateRangeSettings === null) return;
    setDateRangeInDays(getNumberOfDays(dateRangeSettings));
  }, [dateRangeSettings]);

  useEffect(() => {
    if (dateRangeInDays === undefined) return;
    if (!isResolutionViable(resolution, dateRangeInDays)) {
      const newResolution = getFirstViableResolution(dateRangeInDays);
      if (rootRef !== null && rootRef.current) {
        // this is an ugly temporary workaround - ToggleGroup component doesn't allow for re-initiating its
        // state when isSelected is externally modified. Once it's implemented in the component it
        // should be then removed and setResolution(newResolution) can be used instead
        const element = rootRef.current.querySelector(`[data-value="${newResolution.value}"]`);
        if (element && element instanceof HTMLElement) {
          element.click();
        }
      }
    }
  }, [dateRangeInDays, resolution]);

  const chartData = useMemo(() => {
    if (!scoresPerResolution) return {};

    const dateStrings: string[] = [];
    const npsScores: string[] = [];
    let scoreForTimePeriod: Record<string, number>;

    switch (resolution.value) {
      case 'monthly':
        scoreForTimePeriod = scoresPerResolution.scorePerMonth;
        break;
      case 'weekly':
        scoreForTimePeriod = scoresPerResolution.scorePerWeek;
        break;
      default:
        scoreForTimePeriod = scoresPerResolution.scorePerDay;
        break;
    }

    Object.keys(scoreForTimePeriod).forEach((key) => {
      npsScores.push(scoreForTimePeriod[key].toString());

      // C3 doesn't accept dates with time + timezone.
      dateStrings.push(new Date(Number(key)).toISOString().split('T')[0]);
    });

    return { dateStrings, npsScores };
  }, [scoresPerResolution, resolution]);

  return (
    <Container ref={rootRef}>
      <Resolutions>
        <StyledText as="p" textType="body2">
          <NPSTag />
          &nbsp;
          {dateRangeSettings?.type === DATE_RANGE_TYPE.RELATIVE && (
            <Tooltip text={t('date-tooltip-text')}>
              <span>
                {t('date-relative-text', {
                  amount: dateRangeSettings.amount,
                  unit: dateRangeSettings.unit,
                })}
              </span>
            </Tooltip>
          )}
          {dateRangeSettings?.type === DATE_RANGE_TYPE.ABSOLUTE && (
            <Tooltip text={t('date-tooltip-text')}>
              <span>
                {t('date-absolute-text', {
                  from: moment.utc(dateRangeSettings.from).local().format('MMM D, YYYY'),
                  to: moment
                    .utc(dateRangeSettings.to)
                    .subtract(1, 'days')
                    .local()
                    .format('MMM D, YYYY'),
                })}
              </span>
            </Tooltip>
          )}
        </StyledText>
        <ToggleGroup
          description={t('chart-toggle-description')}
          selectedValue={resolution.value}
          onChange={(e) => {
            setResolution(resolutionChoices.find((res) => res.value === e) as NPSResolutionChoice);
          }}
        >
          {resolutionChoices.map((resolutionChoice) => {
            return (
              <ToggleGroup.Toggle
                data-value={resolutionChoice.value}
                key={resolutionChoice.value}
                isDisabled={
                  dateRangeInDays !== undefined &&
                  !isResolutionViable(resolutionChoice, dateRangeInDays)
                }
                value={resolutionChoice.value}
              >
                {t(resolutionChoice.label)}
              </ToggleGroup.Toggle>
            );
          })}
        </ToggleGroup>
      </Resolutions>
      <NPSChart {...chartData} dateRangeInDays={dateRangeInDays} resolution={resolution.value} />
    </Container>
  );
}
